import { Autocomplete, Box, FormGroup, TextField, Typography } from '@mui/material';
import { FilterOption, FilterState } from 'components/featured/Deals/types';
import { createInitialFilterState } from 'components/featured/Deals/utils';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDateRangePicker from 'components/shared/CustomDateRangePicker/CustomDateRangePicker';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import zeplinColor from 'theme/Color';
import { hasActiveFilters } from '../DealsFilters.utils';
import { useBusinessOptions } from '../hooks/useBusinessOptions';
import { useClientOptions } from '../hooks/useClientOptions';
import { useDealTypesOptions } from '../hooks/useDealTypesOptions';
import { useOwnerOptions } from '../hooks/useOwnerOptions';
import { FilterPopoverProps } from '../types';

const DEAL_HEALTH_OPTIONS: FilterOption[] = [
  { Name: 'On track', Id: 'onTrack' },
  { Name: 'Warning', Id: 'warning' },
  { Name: 'Overdue', Id: 'overdue' },
];

export const FilterPopover = ({
  anchorEl,
  onClose,
  filterState,
  onFilterChange,
  isOwnerFilterVisible,
  hideClientFilter = false,
  lockedClient = null,
}: FilterPopoverProps) => {
  const [tempFilterState, setTempFilterState] = useState<FilterState>(filterState);

  const { dealTypes } = useDealTypesOptions();
  const { ownerOptions } = useOwnerOptions();
  const { clientOptions } = useClientOptions();
  const { businessOptions } = useBusinessOptions();

  useEffect(() => {
    if (anchorEl) {
      setTempFilterState(filterState);
    }
  }, [anchorEl, filterState]);

  const activeFilters = hasActiveFilters(tempFilterState);

  const updateTempFilter = (updates: Partial<FilterState>) => {
    setTempFilterState((prev) => ({ ...prev, ...updates }));
  };

  const handleHealthSelection = (_: SyntheticEvent, newValue: any) => {
    console.log(newValue);
    setTempFilterState((prev) => {
      return { ...prev, health: newValue };
    });
  };

  const handleOwnerSelection = (_: SyntheticEvent, newValue: any) => {
    setTempFilterState((prev) => {
      return { ...prev, owner: newValue };
    });
  };

  const handleClientSelection = (_: SyntheticEvent, newValue: any) => {
    setTempFilterState((prev) => {
      return { ...prev, client: newValue };
    });
  };

  const handleBusinessSelection = (_: SyntheticEvent, newValue: any) => {
    setTempFilterState((prev) => {
      return { ...prev, business: newValue };
    });
  };

  const handleDealTypeSelection = (_: SyntheticEvent, newValue: any) => {
    setTempFilterState((prev) => {
      return { ...prev, dealType: newValue };
    });
  };

  const handleClearAll = () => {
    const clearedState = createInitialFilterState();
    if (lockedClient) {
      clearedState.client = [lockedClient];
    }
    setTempFilterState(clearedState);
    onFilterChange(clearedState);
    onClose();
  };

  const handleApply = () => {
    const newFilterState: Partial<FilterState> = {
      health: tempFilterState.health ?? null,
      owner: tempFilterState.owner ?? null,
      client: lockedClient ? [lockedClient] : (tempFilterState.client ?? null),
      business: tempFilterState.business ?? null,
      dealType: tempFilterState.dealType ?? null,
      startDate: tempFilterState.startDate ?? null,
      closeDate: tempFilterState.closeDate ?? null,
    };
    onFilterChange(newFilterState);
    onClose();
  };

  const handleSetAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>> = (
    el: React.SetStateAction<HTMLButtonElement | null>,
  ) => {
    const newValue = typeof el === 'function' ? el(anchorEl) : el;
    if (!newValue) {
      onClose();
    }
  };

  return (
    <CustomPopOver
      setAnchorEl={handleSetAnchorEl}
      anchorEl={anchorEl}
      id='simple-popover'
      sx={{
        borderRadius: '14px',
        boxShadow: 'rgba(0, 0, 0, 0.15)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            padding: '16px',
            width: { xs: '350px', xl: '483px' },
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            height: '100%',
            overflow: 'auto',
            '& .MuiAutocomplete-root': {
              minHeight: '48px',
            },
            '& .MuiInputBase-root': {
              minHeight: '48px',
              overflow: 'visible',
              alignItems: 'flex-start',
            },
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
              minHeight: '48px',
              paddingTop: '6px',
              paddingBottom: '6px',
              alignItems: 'center',
              gap: '6px',
              overflowX: 'auto',
              overflowY: 'hidden',
            },
            '& .MuiFormControl-root': {
              marginBottom: '0px',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
              Filter
            </Typography>
            <Typography
              variant='responsiveButtonSmall'
              color={activeFilters ? zeplinColor.Brand1 : zeplinColor.Neutral8}
              onClick={activeFilters ? handleClearAll : undefined}
              sx={{
                cursor: activeFilters ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Clear All
            </Typography>
          </Box>

          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              width: '100%',
            }}
          >
            <Box sx={{ width: '100%', minHeight: '48px' }}>
              <Autocomplete
                multiple
                value={Array.isArray(tempFilterState.health) ? tempFilterState.health : []}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      label={<Typography variant='responsiveBody2'>Deal Health</Typography>}
                    />
                  );
                }}
                onChange={handleHealthSelection}
                options={DEAL_HEALTH_OPTIONS}
                getOptionKey={(option) => option.Id}
                getOptionLabel={(option) => option.Name}
                isOptionEqualToValue={(option, value) => value.Id === option.Id}
              />
            </Box>

            {isOwnerFilterVisible && (
              <Box sx={{ width: '100%', minHeight: '48px' }}>
                <Autocomplete
                  multiple
                  value={tempFilterState.owner}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        label={<Typography variant='responsiveBody2'>Owner</Typography>}
                      />
                    );
                  }}
                  onChange={handleOwnerSelection}
                  options={ownerOptions}
                  getOptionKey={(option) => option.Id}
                  getOptionLabel={(option) => option.Name}
                  isOptionEqualToValue={(option, value) => value.Id === option.Id}
                />
              </Box>
            )}

            <Box sx={{ width: '100%', minHeight: '48px' }}>
              <Autocomplete
                multiple
                value={tempFilterState.business}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      label={<Typography variant='responsiveBody2'>Company</Typography>}
                    />
                  );
                }}
                onChange={handleBusinessSelection}
                options={businessOptions}
                getOptionKey={(option) => option.Id}
                getOptionLabel={(option) => option.Name}
                isOptionEqualToValue={(option, value) => value.Id === option.Id}
              />
            </Box>

            <Box sx={{ width: '100%', minHeight: '48px' }}>
              <Autocomplete
                multiple
                value={tempFilterState.dealType}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      label={<Typography variant='responsiveBody2'>Deal Type</Typography>}
                    />
                  );
                }}
                onChange={handleDealTypeSelection}
                options={dealTypes}
                getOptionKey={(option) => option.Id}
                getOptionLabel={(option) => (option.label ? option.label : option.Name)}
                isOptionEqualToValue={(option, value) => value.Id === option.Id}
              />
            </Box>

            {!hideClientFilter && (
              <Box sx={{ width: '100%', minHeight: '48px' }}>
                <Autocomplete
                  multiple
                  value={tempFilterState.client}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<Typography variant='responsiveBody2'>Client</Typography>}
                    />
                  )}
                  onChange={handleClientSelection}
                  options={clientOptions}
                  getOptionKey={(option) => option.Id}
                  getOptionLabel={(option) => option.Name}
                  isOptionEqualToValue={(option, value) => value.Id === option.Id}
                />
              </Box>
            )}

            {/* Date Range */}
            <CustomDateRangePicker
              startDate={tempFilterState.startDate}
              closeDate={tempFilterState.closeDate}
              onStartDateChange={(date) => {
                updateTempFilter({ startDate: date });
              }}
              onCloseDateChange={(date) => {
                updateTempFilter({ closeDate: date });
              }}
              label='Close date'
              placeholder='Close date'
              size='medium'
            />
          </FormGroup>

          <Box sx={{ padding: '16px', paddingTop: 0 }}>
            <CustomButton
              disabled={false}
              variant='contained'
              text='Apply'
              onClick={handleApply}
              style={{
                borderRadius: '14px',
                height: '48px',
                width: '100%',
              }}
              buttonVariant='buttonMedium'
            />
          </Box>
        </Box>
      </Box>
    </CustomPopOver>
  );
};
